import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Camera, Eye, EyeOff } from "lucide-react";

export default function Profile() {
  const [userId, setUserId] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [position, setPosition] = useState("");
  const [phone, setPhone] = useState("");
  const [photoUrl, setPhotoUrl] = useState<string>("");
  const [photoPreview, setPhotoPreview] = useState<string>("");
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSubmitting, setPasswordSubmitting] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [emailError, setEmailError] = useState<string | null>(null);
  const [emailSubmitting, setEmailSubmitting] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [emailChangePassword, setEmailChangePassword] = useState("");
  const [showEmailChangePassword, setShowEmailChangePassword] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    (async () => {
      const { data: { session } } = await supabase.auth.getSession();
      const uid = session?.user?.id;
      if (!uid) { setLoading(false); return; }
      setUserId(uid);
      const { data: profile } = await supabase
        .from("profiles")
        .select("first_name, last_name, company_position, personal_phone_number, profile_photo_url, email")
        .eq("id", uid)
        .single();
      if (profile) {
        setFirstName(profile.first_name ?? "");
        setLastName(profile.last_name ?? "");
        setPosition(profile.company_position ?? "");
        setPhone(profile.personal_phone_number ?? "");
        setPhotoUrl(profile.profile_photo_url ?? "");
        setEmail(profile.email ?? session?.user?.email ?? "");
      } else {
        setEmail(session?.user?.email ?? "");
      }
      setLoading(false);
    })();
  }, []);

  const initials = (): string => {
    const f = firstName.trim();
    const l = lastName.trim();
    if (f || l) return `${f.charAt(0)}${l.charAt(0)}`.toUpperCase();
    return (email || "?").charAt(0).toUpperCase();
  };

  const handlePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoFile(file);
    const reader = new FileReader();
    reader.onload = () => {
      setPhotoPreview(typeof reader.result === "string" ? reader.result : "");
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!firstName.trim() || !lastName.trim()) {
      setError("First name and last name are required.");
      return;
    }
    if (!userId) {
      setError("No active session.");
      return;
    }
    setSubmitting(true);
    try {
      let nextPhotoUrl = photoUrl;
      if (photoFile) {
        const ext = (photoFile.name.split(".").pop() || "png").toLowerCase();
        const path = `${userId}/avatar.${ext}`;
        const { error: uploadErr } = await supabase.storage
          .from("profile-photos")
          .upload(path, photoFile, { upsert: true, contentType: photoFile.type });
        if (uploadErr) throw uploadErr;
        const { data: pub } = supabase.storage.from("profile-photos").getPublicUrl(path);
        nextPhotoUrl = pub.publicUrl;
      }

      const { error: updateErr } = await supabase
        .from("profiles")
        .update({
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          company_position: position.trim(),
          personal_phone_number: phone.trim(),
          profile_photo_url: nextPhotoUrl,
          updated_at: new Date().toISOString(),
        })
        .eq("id", userId)
        .select();
      if (updateErr) throw updateErr;

      setPhotoUrl(nextPhotoUrl);
      setPhotoFile(null);
      setPhotoPreview("");
      toast.success("Profile updated");
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to update profile";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const displayPhoto = photoPreview || photoUrl;

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError(null);
    if (newPassword.length < 8) {
      setPasswordError("Password must be at least 8 characters.");
      return;
    }
    if (newPassword !== confirmNewPassword) {
      setPasswordError("Passwords do not match.");
      return;
    }
    setPasswordSubmitting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const userEmail = session?.user?.email;
      if (!userEmail) throw new Error("No active session.");
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: userEmail,
        password: currentPassword,
      });
      if (signInError) {
        setPasswordError("Current password is incorrect.");
        return;
      }
      const { error: updateError } = await supabase.auth.updateUser({ password: newPassword });
      if (updateError) {
        if (updateError.name === "AuthRetryableFetchError" || updateError.message?.includes("Failed to fetch")) {
          toast.success("Password updated successfully.");
          setCurrentPassword("");
          setNewPassword("");
          setConfirmNewPassword("");
          return;
        }
        throw updateError;
      }
      toast.success("Password updated successfully.");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmNewPassword("");
    } catch (err) {
      setPasswordError(err instanceof Error ? err.message : "Failed to update password.");
    } finally {
      setPasswordSubmitting(false);
    }
  };

  const handleEmailChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setEmailError(null);

    if (!newEmail.trim()) {
      setEmailError("Please enter a new email address.");
      return;
    }
    if (newEmail.toLowerCase() === email.toLowerCase()) {
      setEmailError("New email must be different from your current email.");
      return;
    }
    if (!emailChangePassword) {
      setEmailError("Please enter your current password to confirm this change.");
      return;
    }

    setEmailSubmitting(true);
    try {
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password: emailChangePassword,
      });
      if (signInError) {
        setEmailError("Current password is incorrect.");
        return;
      }
      const { error } = await supabase.auth.updateUser(
        { email: newEmail.trim() },
        { emailRedirectTo: 'https://admin.doyencegroup.com/email-change-success' }
      );
      if (error) throw error;
      setEmailSent(true);
      toast.success("Confirmation email sent to your new address. Click the link to complete the change.");
      setNewEmail("");
      setEmailChangePassword("");
    } catch (err) {
      setEmailError(err instanceof Error ? err.message : "Failed to send confirmation email.");
    } finally {
      setEmailSubmitting(false);
    }
  };

  return (
    <>
      <div className="max-w-lg mx-auto">
        <div className="bg-slate-900 border border-slate-800 rounded-lg p-6">
          {loading ? (
            <div className="text-slate-400 text-sm">Loading…</div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Photo */}
              <div className="flex items-center gap-4">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="relative group rounded-full h-20 w-20 overflow-hidden bg-slate-700 flex items-center justify-center text-white text-xl font-semibold border border-slate-700 hover:border-slate-500 transition-colors"
                  aria-label="Change profile photo"
                >
                  {displayPhoto ? (
                    <img src={displayPhoto} alt="Profile" className="h-full w-full object-cover" />
                  ) : (
                    <span>{initials()}</span>
                  )}
                  <span className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                    <Camera className="h-5 w-5 text-white" />
                  </span>
                </button>
                <div className="flex flex-col gap-1">
                  <span className="text-sm text-slate-200 font-medium">Profile photo</span>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="text-xs text-amber-400 hover:text-amber-300 text-left"
                  >
                    Upload new photo
                  </button>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handlePhotoSelect}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="firstName" className="text-slate-300">First name</Label>
                  <Input
                    id="firstName"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                    className="bg-slate-950 border-slate-700 text-slate-100"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="lastName" className="text-slate-300">Last name</Label>
                  <Input
                    id="lastName"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    required
                    className="bg-slate-950 border-slate-700 text-slate-100"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="position" className="text-slate-300">Position / Role</Label>
                <Input
                  id="position"
                  value={position}
                  onChange={(e) => setPosition(e.target.value)}
                  placeholder="e.g. Platform Manager, CTO"
                  className="bg-slate-950 border-slate-700 text-slate-100"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="phone" className="text-slate-300">Phone number</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="bg-slate-950 border-slate-700 text-slate-100"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-slate-300">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  readOnly
                  className="bg-slate-950 border-slate-700 text-slate-400 cursor-not-allowed"
                />
                <p className="text-xs text-slate-500">Email cannot be changed here.</p>
              </div>

              {error && (
                <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded px-3 py-2">
                  {error}
                </div>
              )}

              <div className="flex justify-end pt-2">
                <Button
                  type="submit"
                  disabled={submitting}
                  className="bg-amber-500 hover:bg-amber-400 text-slate-950"
                >
                  {submitting ? "Saving…" : "Save changes"}
                </Button>
              </div>
            </form>
          )}
        </div>

        <div className="bg-slate-900 border border-slate-800 rounded-lg p-6 mt-6">
          <h2 className="text-white font-semibold mb-2">Change Email</h2>
          <p className="text-sm text-slate-400 mb-4">
            Enter your new email address and current password. A confirmation link will be sent to your new address — click it to complete the change.
          </p>

          {emailSent ? (
            <div className="bg-green-500/10 border border-green-500/30 rounded px-3 py-2 text-sm text-green-400">
              A confirmation link has been sent to your new email address. Click the link in that email to complete the change.
            </div>
          ) : (
            <form onSubmit={handleEmailChange} className="space-y-4">
              <div className="space-y-1.5">
                <Label className="text-slate-300">Current email</Label>
                <Input
                  type="email"
                  value={email}
                  readOnly
                  className="bg-slate-950 border-slate-700 text-slate-400 cursor-not-allowed"
                />
              </div>

              <div className="space-y-1.5">
                <Label className="text-slate-300">New email address</Label>
                <Input
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  required
                  placeholder="new@example.com"
                  className="bg-slate-950 border-slate-700 text-slate-100"
                />
              </div>

              <div className="space-y-1.5">
                <Label className="text-slate-300">Current password</Label>
                <div className="relative">
                  <Input
                    type={showEmailChangePassword ? "text" : "password"}
                    value={emailChangePassword}
                    onChange={(e) => setEmailChangePassword(e.target.value)}
                    required
                    placeholder="Enter your current password"
                    className="bg-slate-950 border-slate-700 text-slate-100 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowEmailChangePassword(prev => !prev)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200"
                  >
                    {showEmailChangePassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              {emailError && (
                <p className="text-sm text-red-400">{emailError}</p>
              )}
              <Button
                type="submit"
                disabled={emailSubmitting}
                className="bg-amber-500 hover:bg-amber-600 text-slate-950"
              >
                {emailSubmitting ? "Sending…" : "Send confirmation email"}
              </Button>
            </form>
          )}
        </div>

        <div className="bg-slate-900 border border-slate-800 rounded-lg p-6 mt-6">
          <h2 className="text-white font-semibold mb-4">Change Password</h2>
          <form onSubmit={handlePasswordChange} className="space-y-4">
            <div className="space-y-1">
              <Label className="text-slate-300">Current password</Label>
              <div className="relative">
                <Input
                  type={showCurrentPassword ? "text" : "password"}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  required
                  className="bg-slate-950 border-slate-700 text-slate-100 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword((prev) => !prev)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200"
                >
                  {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-slate-300">New password</Label>
              <div className="relative">
                <Input
                  type={showNewPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  className="bg-slate-950 border-slate-700 text-slate-100 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword((prev) => !prev)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200"
                >
                  {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-slate-300">Confirm new password</Label>
              <Input
                type={showNewPassword ? "text" : "password"}
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
                required
                className="bg-slate-950 border-slate-700 text-slate-100"
              />
            </div>
            {passwordError && (
              <p className="text-sm text-red-400">{passwordError}</p>
            )}
            <Button
              type="submit"
              disabled={passwordSubmitting}
              className="bg-amber-500 hover:bg-amber-600 text-slate-950"
            >
              {passwordSubmitting ? "Updating…" : "Update password"}
            </Button>
          </form>
        </div>
      </div>
    </>
  );
}
